import {generateId} from "../../../../../utils/generate-id";
import {FloorPlanItem} from "../types";

export type TableGroupKind = 'combination' | 'notBookable';

export interface TableGroupTableRef {
    tableId: string;
    resourceId: string;
}

export interface StoredTableGroup {
    id: string;
    kind: TableGroupKind;
    tableIds: string[];
    resourceIds: string[];
    label?: string;
    min?: number;
    max?: number;
    order: number;
}

export interface TableOverlayLike {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface TableGroupOverlay {
    id: string;
    kind: TableGroupKind;
    tableIds: string[];
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

const TABLE_GROUP_PADDING_WITH_SEATS = 24;
const TABLE_GROUP_PADDING_WITHOUT_SEATS = 8;

export const getTableGroupPadding = (showSeatsAroundTable: boolean): number =>
    showSeatsAroundTable ? TABLE_GROUP_PADDING_WITH_SEATS : TABLE_GROUP_PADDING_WITHOUT_SEATS;

const DEFAULT_PADDING = TABLE_GROUP_PADDING_WITHOUT_SEATS;

export const getTableGroupLabel = (kind: TableGroupKind): string =>
    kind === 'combination'
        ? 'Combination'
        : 'Not Bookable';

export const getTableGroupMemberId = (item: FloorPlanItem | undefined | null): string | null => {
    if (!item?.table) return null;
    return item.table.canvas.id ?? item.table.resource?.id ?? item.id ?? null;
};

export const getTableGroupResourceId = (item: FloorPlanItem | undefined | null): string | null => {
    if (!item?.table) return null;
    return item.table.resource?.id ?? item.table.canvas.id ?? item.id ?? null;
};

// Treat a selection as the same group only when it contains the exact same table ids.
export const tableIdSetMatches = (groupTableIds: string[], selectedTableIds: string[]) => {
    if (groupTableIds.length !== selectedTableIds.length) return false;
    const selected = new Set(selectedTableIds);
    return groupTableIds.every((tableId) => selected.has(tableId));
};

const isTableGroupKind = (value: unknown): value is TableGroupKind =>
    value === 'combination' || value === 'notBookable';

const normalizeTableGroupRefs = (
    tableIds: unknown,
    resourceIds: unknown,
): TableGroupTableRef[] => {
    const rawTableIds = Array.isArray(tableIds)
        ? tableIds.filter((tableId: unknown): tableId is string => typeof tableId === 'string' && tableId.trim().length > 0)
        : [];
    const rawResourceIds = Array.isArray(resourceIds) ? resourceIds : [];
    const seenTableIds = new Set<string>();

    return rawTableIds.reduce<TableGroupTableRef[]>((refs, tableId, index) => {
        if (seenTableIds.has(tableId)) return refs;
        seenTableIds.add(tableId);

        const resourceId = typeof rawResourceIds[index] === 'string' && rawResourceIds[index].trim().length > 0
            ? rawResourceIds[index]
            : tableId;

        refs.push({tableId, resourceId});
        return refs;
    }, []);
};

const splitTableGroupRefs = (
    refs: TableGroupTableRef[],
): Pick<StoredTableGroup, 'tableIds' | 'resourceIds'> => ({
    tableIds: refs.map((ref) => ref.tableId),
    resourceIds: refs.map((ref) => ref.resourceId),
});

const normalizePositiveInteger = (value: unknown): number | undefined => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
    const normalized = Math.floor(value);
    return normalized >= 0 ? normalized : undefined;
};

const normalizeCombinationBounds = (
    kind: TableGroupKind,
    min?: number,
    max?: number,
): Pick<StoredTableGroup, 'min' | 'max'> => {
    if (kind !== 'combination') return {};

    const nextMin = normalizePositiveInteger(min);
    const nextMax = normalizePositiveInteger(max);

    if (nextMin == null && nextMax == null) return {};
    if (nextMin != null && nextMax != null && nextMax < nextMin) {
        return {min: nextMin, max: nextMin};
    }

    return {
        ...(nextMin != null ? {min: nextMin} : {}),
        ...(nextMax != null ? {max: nextMax} : {}),
    };
};

const sortTableGroups = (groups: StoredTableGroup[]): StoredTableGroup[] =>
    [...groups].sort((a, b) => a.order - b.order);

const getNextTableGroupOrder = (groups: StoredTableGroup[]): number =>
    groups.reduce((maxOrder, group) => Math.max(maxOrder, group.order), -1) + 1;

export const getStoredTableGroups = (meta: Record<string, any> | undefined | null): StoredTableGroup[] => {
    const raw = meta?.tableGroups;
    if (!Array.isArray(raw)) return [];

    return raw
        .map((entry, index): StoredTableGroup | null => {
            if (!entry || typeof entry !== 'object') return null;
            const id = typeof entry.id === 'string' && entry.id.trim() ? entry.id : generateId();
            const kind = isTableGroupKind(entry.kind) ? entry.kind : null;
            const refs = normalizeTableGroupRefs(entry.tableIds, entry.resourceIds);

            if (!kind || refs.length < 2) return null;

            return {
                id,
                kind,
                ...splitTableGroupRefs(refs),
                label: typeof entry.label === 'string' && entry.label.trim() ? entry.label : undefined,
                order: normalizePositiveInteger(entry.order) ?? index,
                ...normalizeCombinationBounds(
                    kind,
                    normalizePositiveInteger(entry.min),
                    normalizePositiveInteger(entry.max),
                ),
            };
        })
        .filter((entry): entry is StoredTableGroup => entry !== null)
        .sort((a, b) => a.order - b.order);
};

export const removeTablesFromGroups = (
    groups: StoredTableGroup[],
    tableIds: string[],
    keepGroupId?: string
): StoredTableGroup[] => {
    const removalSet = new Set(tableIds);

    return groups
        .map((group) => {
            if (group.id === keepGroupId) return group;
            const nextRefs = normalizeTableGroupRefs(group.tableIds, group.resourceIds)
                .filter((ref) => !removalSet.has(ref.tableId));
            return {
                ...group,
                ...splitTableGroupRefs(nextRefs),
            };
        })
        .filter((group) => group.tableIds.length >= 2);
};

export const upsertTableGroup = (
    groups: StoredTableGroup[],
    nextGroup: StoredTableGroup
): StoredTableGroup[] => {
    const existingGroup = groups.find((group) => group.id === nextGroup.id);
    const normalizedRefs = normalizeTableGroupRefs(nextGroup.tableIds, nextGroup.resourceIds);
    const normalizedGroup: StoredTableGroup = {
        ...nextGroup,
        ...splitTableGroupRefs(normalizedRefs),
        order: existingGroup?.order ?? nextGroup.order ?? getNextTableGroupOrder(groups),
        ...normalizeCombinationBounds(nextGroup.kind, nextGroup.min, nextGroup.max),
    };

    const withoutConflicts = removeTablesFromGroups(groups, normalizedGroup.tableIds, normalizedGroup.id)
        .filter((group) => group.id !== normalizedGroup.id);

    return sortTableGroups([...withoutConflicts, normalizedGroup]);
};

export const createTableGroup = (
    groups: StoredTableGroup[],
    kind: TableGroupKind,
    tables: TableGroupTableRef[],
    id: string = generateId(),
    options?: Pick<StoredTableGroup, 'min' | 'max'>
): StoredTableGroup[] => {
    return upsertTableGroup(groups, {
        id,
        kind,
        ...splitTableGroupRefs(tables),
        label: getTableGroupLabel(kind),
        order: getNextTableGroupOrder(groups),
        ...normalizeCombinationBounds(kind, options?.min, options?.max),
    });
};

export const syncTableGroupsWithTables = (
    groups: StoredTableGroup[],
    validTables: TableGroupTableRef[],
): StoredTableGroup[] => {
    const validTableMap = new Map(validTables.map((table) => [table.tableId, table.resourceId]));

    return groups
        .map((group) => {
            const nextRefs = normalizeTableGroupRefs(group.tableIds, group.resourceIds)
                .filter((ref) => validTableMap.has(ref.tableId))
                .map((ref) => ({
                    tableId: ref.tableId,
                    resourceId: validTableMap.get(ref.tableId) ?? ref.resourceId,
                }));

            return {
                ...group,
                ...splitTableGroupRefs(nextRefs),
            };
        })
        .filter((group) => group.tableIds.length >= 2)
        .sort((a, b) => a.order - b.order);
};

const buildOverlayForTableIds = (
    id: string,
    kind: TableGroupKind,
    tableIds: string[],
    overlaysById: Map<string, TableOverlayLike>,
    label?: string,
    padding = DEFAULT_PADDING
): TableGroupOverlay | null => {
    const overlays = tableIds
        .map((tableId) => overlaysById.get(tableId))
        .filter((overlay): overlay is TableOverlayLike => !!overlay);

    if (overlays.length < 2) return null;

    const minX = Math.min(...overlays.map((overlay) => overlay.x));
    const minY = Math.min(...overlays.map((overlay) => overlay.y));
    const maxX = Math.max(...overlays.map((overlay) => overlay.x + overlay.width));
    const maxY = Math.max(...overlays.map((overlay) => overlay.y + overlay.height));

    return {
        id,
        kind,
        tableIds,
        label: label ?? getTableGroupLabel(kind),
        x: minX - padding,
        y: minY - padding,
        width: maxX - minX + padding * 2,
        height: maxY - minY + padding * 2,
    };
};

export const buildTableGroupOverlays = (
    groups: StoredTableGroup[],
    tableOverlays: TableOverlayLike[],
    padding = DEFAULT_PADDING
): TableGroupOverlay[] => {
    const overlaysById = new Map(tableOverlays.map((overlay) => [overlay.id, overlay]));

    return groups
        .map((group) => buildOverlayForTableIds(group.id, group.kind, group.tableIds, overlaysById, group.label, padding))
        .filter((overlay): overlay is TableGroupOverlay => overlay !== null);
};

export const buildSelectionOverlay = (
    tableIds: string[],
    tableOverlays: TableOverlayLike[],
    label?: string,
    padding = DEFAULT_PADDING
): TableGroupOverlay | null => {
    const overlaysById = new Map(tableOverlays.map((overlay) => [overlay.id, overlay]));
    // Ad-hoc selections reuse the generic group overlay geometry without becoming a stored group.
    return buildOverlayForTableIds('selection', 'combination', tableIds, overlaysById, label, padding);
};
